/**
 * ehr#743 (V11, "Search everything"): one search bar on the client chart's Profile tab, pinned
 * under the top tabs. It looks through every Profile field (label and value, every section and
 * every added entry) and the chart header (Status, Owner, Added, Client Balance...). Typing only
 * changes the results list (Jay's ruling 32A): no field is hidden and the open section never
 * changes. The list shows the open section's matches first, then the other sections in order,
 * then the header; up to 10, then "N more matches, keep typing to narrow". Picking one opens its
 * section and flashes the field.
 *
 * Dates show as the chart's date boxes show them (03/15/1990), and a date is also found as typed
 * any other usual way: 03-15-1990, 3/15/1990 or 1990-03-15 (a slash, dash or dot reads as one).
 *
 * This is the Profile tab's part of Jay's search. Jay answered on #743 (10-31, #5) that it searches
 * everything, notes and messages too, from every tab; the other tabs come in the next PRs.
 */
import { maskBlobSsn, maskedSsn } from '@ehr/shared';

/** The parts of a Profile field the search reads (ContactDrawer's `Field` fits this shape). */
export type SearchField = {
  key: string;
  label: string;
  type?: string;
  options?: readonly string[];
  otherKey?: string;
  nameKey?: string;
  collapsible?: boolean;
  endsGroup?: boolean;
  hideWhen?: string;
  showWhenAny?: readonly string[];
  defaultChecked?: boolean;
  checks?: readonly { key: string; defaultChecked?: boolean }[];
  aiChecks?: readonly { key: string; defaultChecked?: boolean }[];
  inlineChecks?: readonly { key: string; defaultChecked?: boolean }[];
};
export type SearchSection = { key: string; label: string; fields: readonly SearchField[] };
type Blob = Record<string, Record<string, string> | undefined>;

export type ChartHit = {
  id: string;
  /** 'section': a Profile section with no fields of its own to search (Authorizations, V15). */
  kind: 'field' | 'notes' | 'head' | 'section';
  /** Field label (no required star), "Notes", or the header item's name. */
  label: string;
  /** What the field holds now, as shown on screen (blank when empty). */
  value: string;
  /** Other forms the value is found by, never shown (a date's 1990-03-15 and 3/15/1990). */
  also?: string[];
  /** Where it lives: "Profile › Pharmacy", "Profile › Insurance / Payor #2", "Header". */
  where: string;
  /** Profile hits: the section key, the entry's data key (`insurance#2`) and the field key. */
  section?: string;
  sectionLabel?: string;
  dataKey?: string;
  fieldKey?: string;
  /** Collapsible groups to open (`${dataKey}.${headKey}`) so the field is on screen. */
  opens: string[];
};

export type ChartSearchResult = { shown: ChartHit[]; total: number; more: number };

/** Results shown before the "N more matches" line (V11). */
export const CHART_SEARCH_LIMIT = 10;

const SKIP = new Set(['subhead', 'linkhead', 'file']);
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DAY = /^(\d{2})[-/.](\d{2})[-/.](\d{4})$/;

/** 1990-03-15 → 03/15/1990, as the chart's date boxes (DateField) show it; else unchanged. */
export function usDate(v: string): string {
  const m = ISO_DAY.exec(v);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : v;
}

/**
 * The other ways a person may type a date the chart shows as 03/15/1990 (or 03-15-1990 in the
 * header): 1990-03-15, and 3/15/1990 without the leading zeros. Not a date: none.
 */
export function dateForms(shown: string): string[] {
  const m = US_DAY.exec(shown.trim());
  if (!m) return [];
  const [, mm = '', dd = '', yyyy = ''] = m;
  const short = `${Number(mm)}/${Number(dd)}/${yyyy}`;
  return [`${yyyy}-${mm}-${dd}`, ...(short !== `${mm}/${dd}/${yyyy}` ? [short] : [])];
}

/**
 * Text as the search compares it: lower case, with a slash, dash or dot read as the same mark, so
 * "03/15/1990" finds 03-15-1990. One character for one, so a match sits at the same place in the
 * text as shown.
 */
export function searchText(s: string): string {
  return s.toLowerCase().replace(/[/.-]/g, '-');
}

/** A field's value as the chart shows it. Pickers that store an id pass `display`. */
function shownValue(f: SearchField, raw: string, entry: Record<string, string>): string {
  if (f.type === 'checkbox') return '';
  // ehr#712: an SSN shows masked (last 4) here too, even one its Edit button has revealed.
  if (f.key === 'ssn') return maskedSsn(raw);
  if (f.type === 'checkboxes') {
    const picked = raw ? raw.split('||').filter(Boolean) : [];
    const other = f.otherKey ? (entry[f.otherKey] ?? '').trim() : '';
    return [
      ...picked.filter((p) => !(other && p === 'Other')),
      ...(other ? [`Other: ${other}`] : []),
    ]
      .join(', ')
      .trim();
  }
  if (f.type === 'contact') return f.nameKey ? (entry[f.nameKey] ?? '') : '';
  if (f.type === 'date') return usDate(raw);
  if (f.otherKey && raw === 'Other') {
    const other = (entry[f.otherKey] ?? '').trim();
    return other ? `Other: ${other}` : raw;
  }
  return raw;
}

/**
 * Every Profile field of every section and added entry, in the order the chart shows them. A
 * field hidden by its own switch (a "same as" box ticked) is left out: it is not on screen.
 */
export function buildProfileIndex(
  sections: readonly SearchSection[],
  data: Blob,
  extras: Record<string, readonly number[] | undefined>,
  opts: {
    /** A picker's shown text (a provider id → the provider's name); undefined = the default. */
    display?: (f: SearchField, raw: string, entry: Record<string, string>) => string | undefined;
    /** The title of an added entry, e.g. "Insurance / Payor #2". */
    entryTitle?: (sectionKey: string, ordinal: number) => string;
    /** Sections with a Notes box under the fields (all by default). */
    hasNotes?: (sectionKey: string) => boolean;
  } = {},
): ChartHit[] {
  const out: ChartHit[] = [];
  for (const s of sections) {
    if (!s.fields.length) continue;
    const keys = [s.key, ...(extras[s.key] ?? []).map((n) => `${s.key}#${n}`)];
    keys.forEach((dataKey, idx) => {
      const entry = data[dataKey] ?? {};
      const where =
        idx === 0
          ? `Profile › ${s.label}`
          : `Profile › ${opts.entryTitle?.(s.key, idx + 1) ?? `${s.label} #${idx + 1}`}`;
      const defaults = new Map<string, boolean>();
      for (const f of s.fields) {
        if (f.type === 'checkbox') defaults.set(f.key, !!f.defaultChecked);
        for (const c of [...(f.inlineChecks ?? []), ...(f.checks ?? []), ...(f.aiChecks ?? [])])
          defaults.set(c.key, !!c.defaultChecked);
      }
      const isChecked = (k: string) => {
        const v = entry[k] ?? '';
        return v === 'true' || (v === '' && !!defaults.get(k));
      };
      let head: string | null = null;
      for (const f of s.fields) {
        if (f.type === 'subhead' || f.type === 'linkhead') {
          head = f.collapsible ? f.key : null;
          continue;
        }
        if (f.endsGroup) head = null;
        if (SKIP.has(f.type ?? '')) continue;
        if (f.hideWhen && isChecked(f.hideWhen)) continue;
        if (f.showWhenAny && !f.showWhenAny.some(isChecked)) continue;
        const raw = String(entry[f.key] ?? '');
        const value = (opts.display?.(f, raw, entry) ?? shownValue(f, raw, entry)).trim();
        const also = f.type === 'date' ? dateForms(value) : [];
        const opens = [
          ...(head ? [`${dataKey}.${head}`] : []),
          ...(f.type === 'checkboxes' && f.collapsible ? [`${dataKey}.${f.key}`] : []),
        ];
        out.push({
          id: `f:${dataKey}.${f.key}`,
          kind: 'field',
          label: f.label.replace(/\*$/, '').trim(),
          value,
          ...(also.length ? { also } : {}),
          where,
          section: s.key,
          sectionLabel: s.label,
          dataKey,
          fieldKey: f.key,
          opens,
        });
      }
      if (idx === 0 && (opts.hasNotes?.(s.key) ?? true))
        out.push({
          id: `n:${dataKey}`,
          kind: 'notes',
          label: 'Notes',
          value: String(entry._notes ?? '').trim(),
          where,
          section: s.key,
          sectionLabel: s.label,
          dataKey,
          opens: [],
        });
    });
  }
  return out;
}

/**
 * ehr#743 (V15): a Profile section with nothing of its own in the index (Authorizations, whose
 * records live in their own table), found by its name. Picking it opens the section.
 */
export function sectionHit(section: string, label: string): ChartHit {
  return {
    id: `s:${section}`,
    kind: 'section',
    label,
    value: '',
    where: `Profile › ${label}`,
    section,
    sectionLabel: label,
    opens: [],
  };
}

/** The chart header's items (only the ones the header shows now). */
export function headerIndex(items: readonly { label: string; value: string | null | undefined }[]) {
  return items
    .filter((h) => h.value != null && String(h.value).trim() !== '')
    .map<ChartHit>((h) => {
      const value = String(h.value).trim();
      const also = dateForms(value); // Added 10-31-2026 is found as 10/31/2026 or 2026-10-31 too
      return {
        id: `h:${h.label}`,
        kind: 'head',
        label: h.label,
        value,
        ...(also.length ? { also } : {}),
        where: 'Header',
        opens: [],
      };
    });
}

/**
 * The results list for `query`: label or value matches, or every field of a section whose name
 * matches. The open section first (when on the Profile tab), then the other sections in order,
 * then the header; inside each, label / value matches before section-name-only ones.
 */
export function searchChart(
  items: readonly ChartHit[],
  query: string,
  currentSection: string | null,
  limit = CHART_SEARCH_LIMIT,
): ChartSearchResult {
  const q = searchText(query.trim());
  if (!q) return { shown: [], total: 0, more: 0 };
  const has = (text: string) => searchText(text).includes(q);
  const hits: { h: ChartHit; k: number; r: number }[] = [];
  items.forEach((h, k) => {
    const direct =
      has(h.label) ||
      has(h.value) ||
      !!h.also?.some(has) ||
      (h.kind === 'head' && has(`${h.label}: ${h.value}`));
    const bySection = !direct && !!h.sectionLabel && has(h.sectionLabel);
    if (!direct && !bySection) return;
    const tier = h.kind === 'head' ? 2 : h.section === currentSection ? 0 : 1;
    hits.push({ h, k, r: tier * 2 + (direct ? 0 : 1) });
  });
  hits.sort((a, b) => a.r - b.r || a.k - b.k);
  const shown = hits.slice(0, limit).map((x) => x.h);
  return { shown, total: hits.length, more: hits.length - shown.length };
}

export type Mark = { text: string; hit: boolean };

/** Splits `text` around the first match of `query` (shown in green). */
export function markMatch(text: string, query: string): Mark[] {
  const q = searchText(query.trim());
  const i = q ? searchText(text).indexOf(q) : -1;
  if (i < 0) return [{ text, hit: false }];
  return [
    { text: text.slice(0, i), hit: false },
    { text: text.slice(i, i + q.length), hit: true },
    { text: text.slice(i + q.length), hit: false },
  ].filter((p) => p.text !== '');
}

/** A result's value, marked; one found only by another form of it (1990-03-15) is green whole. */
export function markValue(hit: ChartHit, query: string): Mark[] {
  const parts = markMatch(hit.value, query);
  if (parts.some((p) => p.hit)) return parts;
  const q = searchText(query.trim());
  const byForm = !!q && !!hit.also?.some((a) => searchText(a).includes(q));
  return byForm && hit.value ? [{ text: hit.value, hit: true }] : parts;
}

/** A header result's "Label: value", marked. */
export function markHead(hit: ChartHit, query: string): Mark[] {
  const parts = markMatch(`${hit.label}: ${hit.value}`, query);
  if (parts.some((p) => p.hit)) return parts;
  return [{ text: `${hit.label}: `, hit: false }, ...markValue(hit, query)];
}

/** "3 more matches, keep typing to narrow" (V11 copy). */
export function moreLine(n: number): string {
  return `${n} more ${n === 1 ? 'match' : 'matches'}, keep typing to narrow`;
}

/** The "/" key jumps to the bar, except while typing in a box or with a modifier held. */
export function isSlashShortcut(e: {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  target?: EventTarget | null;
}): boolean {
  if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return false;
  const t = e.target as { closest?: (s: string) => unknown } | null | undefined;
  return !t?.closest?.('input, textarea, select, [contenteditable="true"], [contenteditable=""]');
}

/**
 * How many result rows fit above `limitY` (the footer's top edge): the most rows whose last bottom,
 * plus the "more matches" line when it shows, stays above it. Never fewer than 3 (V11). The list
 * itself never scrolls.
 */
export function rowsThatFit(
  rowBottoms: readonly number[],
  limitY: number,
  moreLineH: number,
  /** The list already ends with a "more matches" line (over 10 results). */
  hasMore: boolean,
): number {
  const n = rowBottoms.length;
  for (let k = n; k > 3; k--) {
    const more = hasMore || k < n;
    if ((rowBottoms[k - 1] ?? 0) + (more ? moreLineH : 0) <= limitY) return k;
  }
  return Math.min(3, n);
}

/** Restarts the green flash on `el` (the field or header item a result points to). */
export function flashEl(el: HTMLElement, cls = 'chart-search-hit', ms = 1800) {
  el.classList.remove(cls);
  void el.offsetWidth; // restart the animation when the same field is picked twice
  el.classList.add(cls);
  window.setTimeout(() => el.classList.remove(cls), ms);
}

/** The element a header result points to: the Status pill, or the value after a label. */
export function headerTarget(root: ParentNode, label: string): HTMLElement | null {
  if (label === 'Status')
    return root.querySelector<HTMLElement>('.drawer-stage-row .status-pill-btn');
  const labels = root.querySelectorAll<HTMLElement>('.drawer-stage-row .drawer-stage-label');
  for (const l of Array.from(labels))
    if (l.textContent?.trim() === label) return (l.nextElementSibling as HTMLElement | null) ?? l;
  return null;
}

/** The element a Profile result points to (needs its section open on screen). */
export function profileTarget(root: ParentNode, hit: ChartHit): HTMLElement | null {
  if (hit.kind === 'section')
    return root.querySelector<HTMLElement>(
      `[data-section="${hit.section ?? ''}"] .demo-section-title`,
    );
  if (hit.kind === 'notes')
    return root.querySelector<HTMLElement>(`[id="notes-${hit.dataKey ?? ''}"]`);
  return root.querySelector<HTMLElement>(
    `[data-entry="${hit.dataKey ?? ''}"] [data-field="${hit.fieldKey ?? ''}"]`,
  );
}

/**
 * ehr#743 (V11): the Profile's Cancel / Save draft / Save record bar shows on the Profile tab, and on
 * any other tab while the Profile has unsaved changes (or a save, or its card, is still running), so
 * leaving the tab never strands the edits.
 */
export function showProfileSaveBar(s: {
  view: string;
  isClient: boolean;
  ready: boolean;
  dirty: boolean;
  busy?: boolean;
}): boolean {
  if (!s.isClient || !s.ready) return false;
  return s.view === 'demographics' || s.dirty || !!s.busy;
}

/**
 * Cancel puts the last saved Profile back. Image previews follow: a preview for an image added
 * since the save goes, and a saved image that was removed or replaced is fetched again.
 */
export function previewsAfterCancel(
  previews: Record<string, string>,
  current: Blob,
  saved: Blob,
  /** The image fields' keys (each has slots `${key}0` and `${key}1`). */
  imageFields: readonly string[],
): { keep: Record<string, string>; refetch: { key: string; fileId: string }[] } {
  const keep: Record<string, string> = {};
  const refetch: { key: string; fileId: string }[] = [];
  const val = (b: Blob, k: string) => {
    const dot = k.lastIndexOf('.');
    return String(b[k.slice(0, dot)]?.[k.slice(dot + 1)] ?? '');
  };
  const isImage = (slot: string) => imageFields.some((f) => slot === `${f}0` || slot === `${f}1`);
  for (const [k, v] of Object.entries(previews)) if (val(current, k) === val(saved, k)) keep[k] = v;
  for (const [sec, entry] of Object.entries(saved))
    for (const [slot, id] of Object.entries(entry ?? {})) {
      const k = `${sec}.${slot}`;
      if (!id || !isImage(slot) || keep[k] || val(current, k) === id) continue;
      refetch.push({ key: k, fileId: id });
    }
  return { keep, refetch };
}

/**
 * ehr#743: what the Profile's Cancel goes back to: the chart as last loaded or saved, every SSN
 * masked (ehr#712: masked once saved). An SSN its Edit button revealed is on screen but is not an
 * unsaved change, so it can be there while nothing is unsaved; Cancel must not bring the full number
 * back (or leave the SSN open for editing).
 */
export function savedProfile<T extends Record<string, unknown>>(data: T): T {
  return maskBlobSsn(data).data;
}
